<template>
  <b-modal
    ref="modal"
    title="Import excel danh mục chỉ tiêu"
    size="lg"
    hide-footer
    :no-close-on-backdrop="isProcessing"
    :no-close-on-esc="isProcessing"
    :hide-header-close="isProcessing"
    @hidden="reset"
  >
    <!-- SRS 10.4, trình bày theo khuôn import excel chung của hệ thống (người dùng chốt 01/10/2026). -->
    <div class="form-group">
      <b>Bước 1:</b> Vui lòng chuẩn bị dữ liệu mẫu từ file excel đúng format, hoặc có thể tải ở
      <a :href="urlExcel">đây</a>
    </div>
    <div class="form-group">
      <b>Bước 2:</b> Chọn file và bấm <i class="fas fa-upload text-primary"></i> để kiểm tra
      <b-form-file
        ref="fileInput"
        v-model="file"
        accept=".xlsx"
        class="d-none"
        @input="result = null"
      ></b-form-file>
      <b-input-group class="pt-2">
        <b-input-group-prepend>
          <b-button
            variant="secondary"
            title="Chọn file"
            :disabled="isProcessing"
            @click="pickFile"
            ><i class="fas fa-paperclip"></i
          ></b-button>
        </b-input-group-prepend>
        <b-form-input
          :value="file ? file.name : ''"
          placeholder="Hãy chọn một tập tin"
          readonly
          class="file-name"
          :disabled="isProcessing"
          @click="pickFile"
        ></b-form-input>
        <b-input-group-append>
          <b-button
            variant="secondary"
            title="Kiểm tra"
            aria-label="Kiểm tra"
            :disabled="!file || isProcessing"
            @click="onCheck"
          >
            <b-spinner v-if="isSubmitting('check')" small />
            <i v-else class="fas fa-upload text-primary"></i>
          </b-button>
          <b-button
            variant="secondary"
            title="Xoá file"
            :disabled="!file || isProcessing"
            @click="clearFile"
            ><i class="fas fa-trash text-danger"></i
          ></b-button>
        </b-input-group-append>
      </b-input-group>
      <label class="text-muted font-size-sm mt-1 mb-0">Chỉ nhận file xlsx và nhỏ hơn 5MB</label>
    </div>
    <div class="form-group mb-0"><b>Bước 3:</b> Lưu khi không còn dòng lỗi</div>

    <template v-if="result">
      <!-- Kết quả "Kiểm tra" (API #11): một bảng chi tiết từng dòng, dòng lỗi xếp trước — người
           dùng chốt 01/10/2026 thay bảng theo danh mục + bảng lỗi của SRS 10.4. -->
      <div class="d-flex align-items-center flex-wrap mt-4 mb-2">
        <h5 class="mb-0 mr-3">Xem trước dữ liệu ({{ result.totalRow }} dòng)</h5>
        <b-badge variant="success" class="mr-1">{{ result.validRow }} hợp lệ</b-badge>
        <b-badge v-if="result.errorRow > 0" variant="danger">{{ result.errorRow }} lỗi</b-badge>
      </div>

      <div class="d-flex align-items-center justify-content-between flex-wrap mb-2">
        <b-form-checkbox
          v-model="showOnlyErrors"
          :disabled="isProcessing || !result.errorRow"
          switch
          class="mb-1"
        >
          Chỉ hiện dòng lỗi
        </b-form-checkbox>
        <span v-if="result.totalRow > result.rows.length" class="text-muted font-size-sm mb-1">
          Hiển thị {{ result.rows.length }} dòng đầu (dòng lỗi xếp trước) / {{ result.totalRow }}
        </span>
      </div>

      <b-table
        :items="pagedRows"
        :fields="rowFields"
        :tbody-tr-class="rowClass"
        small
        bordered
        hover
        responsive
        show-empty
        empty-text="Không có dòng nào"
        class="preview-table"
      >
        <template v-slot:cell(productCode)="row">
          <span class="text-break">{{ row.item.productCode }}</span>
        </template>
        <template v-slot:cell(productName)="row">
          <span class="text-break">{{ row.item.productName }}</span>
        </template>
        <template v-slot:cell(catTarget)="row">
          <span class="text-break">{{ row.item.catTargetCode }}</span>
          <div v-if="row.item.catTargetName" class="text-muted font-size-sm text-break">
            {{ row.item.catTargetName }}
          </div>
        </template>
        <template v-slot:cell(effectiveFrom)="row">
          {{ toClientDate(row.item) }}
        </template>
        <template v-slot:cell(message)="row">
          <span v-if="row.item.errorCode" class="text-danger">
            <i class="fas fa-exclamation-triangle text-danger mr-1"></i>{{ row.item.message }}
          </span>
          <span v-else class="text-success"><i class="fas fa-check text-success mr-1"></i>Hợp lệ</span>
        </template>
      </b-table>
      <div
        v-if="filteredRows.length > perPage"
        class="d-flex align-items-center justify-content-between flex-wrap mt-2 custom-pagination"
      >
        <div class="mb-1">Hiển thị {{ startRow }}–{{ endRow }} / {{ filteredRows.length }} dòng</div>
        <b-pagination
          v-model="currentPage"
          :total-rows="filteredRows.length"
          :per-page="perPage"
          size="sm"
          align="right"
          class="mb-1"
        />
      </div>
    </template>

    <div class="d-flex flex-wrap mt-4">
      <!-- SRS 10.4: chỉ ghi khi errorRow = 0 (nhãn "Lưu" theo khuôn import chung, người dùng chốt). -->
      <SaveButton
        type="button"
        text="Lưu"
        class="mr-3 mb-1"
        :is-busy="isSubmitting('commit')"
        :disabled="!canCommit"
        @click="onCommit"
      />
      <b-button
        size="sm"
        variant="secondary"
        class="mb-1"
        :disabled="isProcessing"
        @click="$refs.modal.hide()"
        >Hủy</b-button
      >
    </div>
  </b-modal>
</template>

<script>
import ApiService from '@/core/services/api.service';
import SaveButton from '@/view/components/common/SaveButton.vue';
import submitLockMixin from '@/view/mixins/submit-lock.mixins';
import { makeToastFaile, makeToastSuccess, getApiErrorMessage } from '@/utils/common';
import { BASE_URL } from '@/utils/constants';
import { DATE_FORMAT, YEAR_MONTH_DAY_FORMAT, formatSpecificDate } from '@/utils/date';

// t102962 — hộp "Nạp tệp điền lần đầu go-live" (SRS 10.4; FR-801, FR-802; kỹ thuật #4).
const API = 'v2/cat-targets/go-live-import';
// File mẫu nằm ở API `src/media/upload/cat-target/` (cùng chỗ các file mẫu import khác).
const TEMPLATE_PATH = 'media/upload/cat-target/Temp_Import_Cat_Target_Go_Live.xlsx';
// FR-802 (câu SRS, "Ghi" → "Lưu" theo nhãn nút): hết thời gian chờ khi máy chủ có thể vẫn ghi.
const IMPORT_FAILED_CODE = 'CAT_TARGET_IMPORT_FAILED';
// Ngày sai định dạng thì hiện nguyên văn trong tệp (không đổi được sang dd/mm/yyyy).
const INVALID_DATE_CODE = 'INVALID_DATE';
const TIMEOUT_MESSAGE =
  'Thao tác đang chạy lâu. Vui lòng đợi vài phút rồi bấm Kiểm tra lại, không bấm Lưu lại.';

export default {
  mixins: [submitLockMixin],
  components: { SaveButton },
  data() {
    return {
      file: null,
      result: null,
      urlExcel: BASE_URL + TEMPLATE_PATH,
      rowFields: [
        { key: 'productCode', label: 'Mã sản phẩm' },
        { key: 'productName', label: 'Tên sản phẩm' },
        { key: 'catTarget', label: 'Danh mục chỉ tiêu' },
        { key: 'effectiveFrom', label: 'Hiệu lực từ', class: 'text-center text-nowrap' },
        { key: 'message', label: 'Trạng thái' },
      ],
      showOnlyErrors: false,
      perPage: 10,
      currentPage: 1,
    };
  },
  watch: {
    // Kết quả kiểm tra mới hoặc đổi bộ lọc → về trang đầu.
    result() {
      this.showOnlyErrors = false;
      this.currentPage = 1;
    },
    showOnlyErrors() {
      this.currentPage = 1;
    },
  },
  computed: {
    filteredRows() {
      if (!this.result) {
        return [];
      }
      return this.showOnlyErrors
        ? this.result.rows.filter((item) => item.errorCode)
        : this.result.rows;
    },
    pagedRows() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredRows.slice(start, start + this.perPage);
    },
    startRow() {
      return (this.currentPage - 1) * this.perPage + 1;
    },
    endRow() {
      return Math.min(this.currentPage * this.perPage, this.filteredRows.length);
    },
    // Khóa cả nhóm control khi đang kiểm tra hoặc đang ghi (rule 12 §9.1).
    isProcessing() {
      return this.isSubmitting('check') || this.isSubmitting('commit');
    },
    canCommit() {
      return Boolean(this.file && this.result && this.result.errorRow === 0 && !this.isProcessing);
    },
  },
  methods: {
    show() {
      this.$refs.modal.show();
    },
    rowClass(item) {
      return item && item.errorCode ? 'table-danger' : '';
    },
    toClientDate(item) {
      if (!item.effectiveFrom || item.errorCode === INVALID_DATE_CODE) {
        return item.effectiveFrom;
      }
      return formatSpecificDate(item.effectiveFrom, YEAR_MONTH_DAY_FORMAT, DATE_FORMAT);
    },
    reset() {
      this.file = null;
      this.result = null;
    },
    pickFile() {
      if (!this.isProcessing) {
        this.$refs.fileInput.$el.querySelector('input').click();
      }
    },
    clearFile() {
      this.$refs.fileInput.reset();
      this.reset();
    },
    formData() {
      const data = new FormData();
      data.append('file', this.file);
      return data;
    },
    // FR-801 — không ghi gì.
    onCheck() {
      if (!this.file) {
        return;
      }
      return this.withSubmitLock('check', async () => {
        try {
          const { data } = await ApiService.post(`${API}/preview`, this.formData());
          this.result = data.data;
        } catch (err) {
          this.result = null;
          makeToastFaile(getApiErrorMessage(err));
        }
      });
    },
    // FR-802 — ghi theo lô; hỏng giữa chừng thì các lô trước đã ghi → bấm Kiểm tra lại để biết.
    onCommit() {
      if (!this.canCommit) {
        return;
      }
      return this.withSubmitLock('commit', async () => {
        try {
          const { data } = await ApiService.post(`${API}/commit`, this.formData());
          makeToastSuccess(data.message);
          this.$emit('saved');
          this.$refs.modal.hide();
        } catch (err) {
          const response = err && err.response;
          const body = response && response.data;
          if (!response || response.status === 504) {
            makeToastFaile(TIMEOUT_MESSAGE);
          } else if (body && body.code === IMPORT_FAILED_CODE && body.details) {
            // Kỹ thuật #4 (review P8 #5): ghi theo lô — báo rõ phần ĐÃ ghi. Kiểm tra lại thì các
            // dòng đã ghi báo "đã có khai báo cùng ngày hiệu lực" → bỏ khỏi tệp rồi nạp phần còn lại.
            makeToastFaile(
              `Đã ghi ${body.details.written} dòng, lỗi ở lô ${body.details.failedAtBatch}. ` +
                'Vui lòng bấm Kiểm tra lại để xem các dòng đã ghi.',
            );
          } else {
            makeToastFaile(getApiErrorMessage(err));
          }
          // Kết quả kiểm tra cũ không còn đúng (có thể đã ghi một phần) → buộc Kiểm tra lại.
          this.result = null;
        }
      });
    },
  },
};
</script>

<style scoped>
.file-name {
  cursor: pointer;
  caret-color: transparent;
}
.preview-table >>> td,
.preview-table >>> th {
  vertical-align: middle;
}
</style>
